import React, { useLayoutEffect, useRef, useState, useMemo } from 'react';
import { gsap, Linear } from 'gsap';
import { useIsMounted } from '@hooks/useIsMounted';
import { useScrollController } from '@context/ScrollControllerProvider/useScrollController';
import { Box } from '@components/Box';
import ScrollMagic from 'scrollmagic';
import { useThemeMediaQuery } from '@hooks/useThemeMediaQuery';
import {
  InitialImageWrapper,
  LottieContainer,
  LottieWrapper,
  MainTextPairing,
  MultipleBackgroundContent,
  MultipleBackgroundWrapper,
  MultipleEventsTextPairing,
  OddTextPairingWrapper,
  SectionBox,
  SectionBoxWrapper,
  SectionWrapper,
  MultiImageContainer,
} from './KnowWhoToExpectSection.styles';
import { cloudAssetUrl } from '@utils/cloudAssetUrl.util';
import { useLottieAnimation } from '@hooks/useLottieAnimation';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { useTranslation } from 'react-i18next';

export const KnowWhoToExpectSection = () => {
  const { t } = useTranslation('onlineRsvp');
  const eventsData = t('events');
  const knowWhoToExpect = t('knowWhoToExpect');
  const sectionRef = useRef<HTMLDivElement>(null);
  const lottieRef = useRef<HTMLDivElement>(null);
  const scrollingTextRef = useRef<HTMLDivElement>(null);
  const [isScrolledIntoView, setScrolledIntoView] = useState(false);
  const scrollController = useScrollController();
  const isMounted = useIsMounted();
  const isMobile = useThemeMediaQuery('below_sm');
  const { animation } = t('knowWhoToExpect');

  // Make the config reactive to animation path changes
  const lottieConfig = useMemo(
    () => ({
      path: cloudAssetUrl(animation), // Required
      renderer: 'svg' as const, // Required
      loop: false, // Optional
      autoplay: false, // Optional
      name: 'Know what features',
      reactivePath: true,
      rendererSettings: {
        progressiveLoad: false,
        preserveAspectRatio: 'xMidYMid slice',
      },
    }),
    [animation]
  );

  const { animationRef, loadAnimation } = useLottieAnimation(lottieConfig, { containerRef: lottieRef, isLoadInitially: false });
  useLayoutEffect(() => {
    let scene: InstanceType<typeof ScrollMagic.Scene> | undefined;
    let knowWhatTimeline: ReturnType<typeof gsap.timeline>;
    let backgroundTimeLine: ReturnType<typeof gsap.timeline>;
    let OddAreTimeline: ReturnType<typeof gsap.timeline>;
    const knowWhatContainerEl = scrollingTextRef.current;

    if (scrollController && isMounted && knowWhatContainerEl) {
      OddAreTimeline = gsap.timeline({ paused: true });
      knowWhatTimeline = gsap.timeline({ paused: true });
      backgroundTimeLine = gsap.timeline({ paused: true });
      backgroundTimeLine.to('#Know_Who_and_What_to_Expect', { opacity: 0, duration: 0.1 }, '+=0.2');
      backgroundTimeLine.to('#Multiple_and_Private_Events', { y: '-170vh' }, '-=0.1');

      knowWhatTimeline
        .to('#knowWhatToExpect', { opacity: 0, display: 'none', duration: 0.2, delay: 0.2 })
        .to('#OddAreWedding', { opacity: 1, display: 'block', duration: 0.2 });
      OddAreTimeline.to(
        { frame: 0 },
        1,
        {
          frame: 1,
          onUpdate: () => {
            animationRef.current?.goToAndStop(Math.round(OddAreTimeline.totalProgress() * 300), true);
          },
          ease: Linear.easeNone,
        },
        0
      );
      knowWhatTimeline.fromTo(
        knowWhatContainerEl,
        1,
        {
          transform: isMobile ? 'translateY(70vh)' : 'translateY(30vh)',
        },
        {
          transform: isMobile ? 'translateY(50vh)' : 'translateY(10vh)',
        },
        0
      );
      scene = new ScrollMagic.Scene({
        triggerElement: '#AnimatedSection',
        triggerHook: 0,
        duration: '300%',
        offset: 250,
      })
        .setPin('#AnimatedSection')
        .addTo(scrollController)
        .on('progress', (event: ScrollMagic.SceneProgressEvent) => {
          knowWhatTimeline.progress(event.progress);
          OddAreTimeline.progress(event.progress);
          backgroundTimeLine.progress(event.progress);
        });
    }

    const clearScene = () => {
      if (knowWhatTimeline) {
        knowWhatTimeline.progress(0);
        knowWhatTimeline.kill();
      }
      if (OddAreTimeline) {
        OddAreTimeline.progress(0);
        OddAreTimeline.kill();
      }
      if (backgroundTimeLine) {
        backgroundTimeLine.progress(0);
        backgroundTimeLine.kill();
      }
      if (scene) {
        scene.destroy(true);
      }
    };
    return clearScene;
  }, [scrollController, isMounted, animationRef, isMobile]);

  return (
    <SectionWrapper id="AnimatedSection" backgroundColor="yellow1" ref={sectionRef}>
      <LazyLoadWrapper
        beforeLoad={() => {
          setScrolledIntoView(true);
          loadAnimation();
        }}
      >
        <div />
      </LazyLoadWrapper>
      <SectionBoxWrapper>
        <SectionBox>
          <InitialImageWrapper isScrolledIntoView={isScrolledIntoView} id="Know_Who_and_What_to_Expect" />
          <MultipleBackgroundWrapper backgroundColor="yellow1" id="Multiple_and_Private_Events">
            <MultipleBackgroundContent size={250} left={3} top={5} respDimension={120}>
              <MultiImageContainer
                src="wedding-invitations/multiple-private-events/multi-event-rsvp-bgmulti-event-rsvp-bg4.webp"
                onErrorImg="wedding-invitations/multiple-private-events/multi-event-rsvp-bgmulti-event-rsvp-bg4.gif"
                alt={knowWhoToExpect.altText}
                width="100%"
                height="100%"
              />
            </MultipleBackgroundContent>
            <MultipleBackgroundContent size={510} left={isMobile ? 70 : 80} top={10} respDimension={200}>
              <MultiImageContainer
                src="wedding-invitations/multiple-private-events/multi-event-rsvp-bgmulti-event-rsvp-bg1.jpg"
                alt={knowWhoToExpect.altText}
                width="100%"
                height="100%"
              />
            </MultipleBackgroundContent>
            <MultipleBackgroundContent size={840} left={isMobile ? -57 : -27} top={33} respDimension={380}>
              <MultiImageContainer
                src="wedding-invitations/multiple-private-events/multi-event-rsvp-bgmulti-event-rsvp-bg2.jpg"
                alt={knowWhoToExpect.altText}
                width="100%"
                height="100%"
              />
            </MultipleBackgroundContent>
            <MultipleBackgroundContent size={465} left={isMobile ? 67 : 77} top={isMobile ? 57 : 67} respDimension={210}>
              <MultiImageContainer
                src="wedding-invitations/multiple-private-events/multi-event-rsvp-bgmulti-event-rsvp-bg3.jpg"
                alt={knowWhoToExpect.altText}
                width="100%"
                height="100%"
              />
            </MultipleBackgroundContent>
          </MultipleBackgroundWrapper>
        </SectionBox>
      </SectionBoxWrapper>
      <Box>
        <Box paddingTop={[5, 5, 15]} ref={scrollingTextRef} style={{ position: 'relative' }}>
          <Box id="knowWhatToExpect">
            <MainTextPairing
              alignment="center"
              title={knowWhoToExpect.title}
              titleTagName="h2"
              titleVariant="hed72Hero"
              titleColor="blue1"
              body={knowWhoToExpect.content}
              bodyVariant="dek20"
              bodyColor="blue1"
            />
          </Box>
          <OddTextPairingWrapper id="OddAreWedding">
            <MultipleEventsTextPairing
              alignment="center"
              eyebrow={eventsData.eyebrow}
              eyebrowTagName="h2"
              eyebrowColor="blue6"
              eyebrowVariant="sectionEyeBrow24"
              title={eventsData.title}
              titleVariant="hed56"
              titleColor="blue6"
              titleTagName="h2"
              body={eventsData.content}
              bodyVariant="featureCopy17"
              bodyColor="blue6"
            />
            {/* <LearnMoreWrapper>
              <LinkWrapper id="multiplePrivateEventsLearnMore" href={getRootUrl('/')} color="#5F269B">
                {eventsData.learnMore}
              </LinkWrapper>
            </LearnMoreWrapper> */}
          </OddTextPairingWrapper>
          <LottieWrapper>
            <LottieContainer ref={lottieRef} />
          </LottieWrapper>
        </Box>
      </Box>
    </SectionWrapper>
  );
};
